import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  buildCommitAdminProspectImportRequest,
  buildConfirmAdminProspectImportMappingRequest,
  buildCreateAdminProspectImportUploadRequest,
  buildGetAdminProspectImportJobRequest,
  buildParseAdminProspectImportFileRequest,
  buildPreviewAdminProspectImportRequest,
  buildSuggestAdminProspectImportMappingRequest,
  cancelAdminProspectImport,
  commitAdminProspectImport,
  confirmAdminProspectImportMapping,
  createAdminProspectImportUpload,
  getAdminProspectImportJob,
  parseAdminProspectImportFile,
  previewAdminProspectImport,
  suggestAdminProspectImportMapping,
  uploadProspectImportSource,
} from '../../services/admin/prospectsService';
import { runExcelImportSession } from '../../services/admin/prospectImportExcelSession';
import { classifyAdminCallableError } from './adminProspectDisplay';
import {
  ADMIN_PROSPECT_IMPORT_MAX_FILE_BYTES,
  DELIMITER_OPTIONS,
  IMPORT_FILE_ACCEPT,
  excelContentTypeForFileType,
  importFileTypeFromName,
  parseProspectImportText,
} from './prospectImportParser';
import {
  IGNORE_TARGET,
  PROPERTY_MAPPING_TARGETS,
  REQUIRED_IMPORT_TARGETS,
  validateMapping,
} from './prospectImportMapping';
import { profileImportColumns, vertexMappingHelpful } from './prospectImportColumnProfile';
import {
  applyOwnerAddressMode,
  mapRowsWithProfile,
  ownerAddressModeFromProfile,
  ownerGroupHasNameSlots,
  previewOwnerMailingAddressFromRow,
  setPrimaryOwnerGroupSlot,
  shouldHideTopLevelOwnerNameSelector,
} from './prospectImportJlr';
import {
  applyReviewedMappingToProfile,
  applyVertexSuggestionToSetup,
  mappingSatisfiedTargets,
  suggestImportSetup,
} from './prospectImportProfile';
import { formatImportPreviewChangeSummary } from './prospectImportPreviewFormat';
import {
  applyBulkUpdateSelectionToggle,
  countConfirmedEligibleUpdates,
  eligibleWillUpdateRowNumbers,
  isPartialUpdatePreviewSample,
  pruneConfirmedUpdates,
  updateSelectionState,
} from './prospectImportPreviewSelection';

const PRIMARY_OWNER_COMPOSITE_SLOTS = [
  { slot: 'address', key: 'admin.prospection.import.owner.slot.address' },
  { slot: 'civicNumber', key: 'admin.prospection.import.owner.slot.civicNumber' },
  { slot: 'street', key: 'admin.prospection.import.owner.slot.street' },
  { slot: 'city', key: 'admin.prospection.import.owner.slot.city' },
  { slot: 'province', key: 'admin.prospection.import.owner.slot.province' },
  { slot: 'postalCode', key: 'admin.prospection.import.owner.slot.postalCode' },
  { slot: 'firstName', key: 'admin.prospection.import.owner.slot.firstName' },
  { slot: 'lastName', key: 'admin.prospection.import.owner.slot.lastName' },
];

function columnIndexForTarget(mapping, target) {
  const index = Array.isArray(mapping) ? mapping.indexOf(target) : -1;
  return index >= 0 ? index : '';
}

function OwnerColumnSelect({ id, label, headers, value, onChange, noneLabel }) {
  return (
    <label className="block text-sm" htmlFor={id}>
      <span className="mb-1 block text-slate-600">{label}</span>
      <select
        id={id}
        value={value === 0 || value ? String(value) : ''}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <option value="">{noneLabel}</option>
        {(headers || []).map((header, index) => (
          <option key={`${header}-${index}`} value={index}>
            {header}
          </option>
        ))}
      </select>
    </label>
  );
}

const STEPS = ['source', 'mapping', 'preview'];
const STEP_LABELS = ['source', 'mapping', 'preview', 'import'];
const IMPORT_ROW_LIMIT = 500;
const ASYNC_POLL_MS = 3000;
const HEADER_MODE_OPTIONS = ['auto', 'yes', 'no'];

function hasHeaderFromMode(headerMode) {
  if (headerMode === 'yes') return true;
  if (headerMode === 'no') return false;
  return null;
}

function isFileInputMode(mode) {
  return mode === 'file_csv' || mode === 'file_excel' || mode === 'csv' || mode === 'xlsx';
}

function fieldErrorKey(field, code) {
  return `admin.prospection.import.error.${field}.${code}`;
}

function rowStatusKey(status) {
  const key = status === 'skipped' ? 'excluded' : status;
  return `admin.prospection.import.status.${key}`;
}

function formatImportFlash(result, t) {
  const inserted = Number(result?.inserted) || 0;
  const skippedDuplicate = Number(result?.skippedDuplicate) || 0;
  const failed = Number(result?.failed) || 0;
  const template =
    failed > 0 ? t('admin.prospection.import.success.failed') : t('admin.prospection.import.success');
  return template
    .replace('{inserted}', String(inserted))
    .replace('{skippedDuplicate}', String(skippedDuplicate))
    .replace('{failed}', String(failed));
}

function isExpiredError(error) {
  if (classifyAdminCallableError(error) !== 'failed-precondition') return false;
  const details = callableDetails(error);
  if (details && details.reason === 'import_expired') return true;
  const message = String(error?.message || error?.code || '');
  return /expir/i.test(message);
}

function callableDetails(error) {
  if (error?.details && typeof error.details === 'object') return error.details;
  if (error?.customData?.details && typeof error.customData.details === 'object') {
    return error.customData.details;
  }
  return null;
}

function countsFromJob(job) {
  const counts = job?.counts && typeof job.counts === 'object' ? job.counts : {};
  return {
    total: Number(job?.rowCount ?? counts.total) || 0,
    ready: Number(job?.readyCount ?? counts.ready) || 0,
    willUpdate: Number(job?.willUpdateCount ?? counts.willUpdate) || 0,
    unchanged: Number(job?.unchangedCount ?? counts.unchanged) || 0,
    invalid: Number(job?.invalidCount ?? counts.invalid) || 0,
    duplicateInFile: Number(job?.duplicateInFileCount ?? counts.duplicateInFile) || 0,
    ambiguousMatch: Number(job?.ambiguousMatchCount ?? counts.ambiguousMatch) || 0,
    conflict: Number(job?.conflictCount ?? counts.conflict) || 0,
    alreadyExists: Number(job?.alreadyExistsCount ?? counts.alreadyExists) || 0,
  };
}

export default function AdminProspectImportPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const bulkUpdateSelectRef = useRef(null);
  const pasteId = useId();
  const fileId = useId();
  const headerId = useId();
  const delimiterId = useId();

  const [step, setStep] = useState('source');
  const [inputMode, setInputMode] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [delimiter, setDelimiter] = useState('auto');
  const [headerMode, setHeaderMode] = useState('auto');
  const [table, setTable] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [importProfile, setImportProfile] = useState(null);
  const [sourceError, setSourceError] = useState('');
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [confirmedUpdates, setConfirmedUpdates] = useState(() => new Set());
  const [matchResolutions, setMatchResolutions] = useState(() => ({}));
  const [busy, setBusy] = useState(false);
  const [vertexBusy, setVertexBusy] = useState(false);
  const [vertexError, setVertexError] = useState('');
  const [vertexSuggestion, setVertexSuggestion] = useState(null);
  const [vertexApplyFeedback, setVertexApplyFeedback] = useState(null);
  const [restoreJlrFeedback, setRestoreJlrFeedback] = useState(false);
  const [ownerAddressMode, setOwnerAddressMode] = useState('single');
  const [expired, setExpired] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState('');
  const [importId, setImportId] = useState(null);
  const [filePhase, setFilePhase] = useState('');
  const [sourceWarnings, setSourceWarnings] = useState([]);
  const [execution, setExecution] = useState('sync');
  const [detectedRowCount, setDetectedRowCount] = useState(0);
  const [pollKind, setPollKind] = useState('');
  const [jobProgress, setJobProgress] = useState(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step, expired]);

  useEffect(() => {
    if (!importId || !pollKind) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await getAdminProspectImportJob(buildGetAdminProspectImportJobRequest({ importId }));
        if (cancelled) return;
        setJobProgress(job);
        if (pollKind === 'preview' && (job.status === 'preview_ready' || job.status === 'previewed')) {
          setPreview({
            importId,
            counts: countsFromJob(job),
            rows: Array.isArray(job.rows) ? job.rows : [],
          });
          setPollKind('');
          setBusy(false);
        }
        if (pollKind === 'import' && job.status === 'completed') {
          setPollKind('');
          navigate('/admin/prospection', {
            state: {
              importResult: {
                inserted: Number(job.insertedRows ?? job.result?.inserted) || 0,
                skippedDuplicate: Number(job.skippedDuplicateRows ?? job.result?.skippedDuplicate) || 0,
                failed: Number(job.failedRows ?? job.result?.failed) || 0,
              },
            },
          });
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          setPollKind('');
          setBusy(false);
          setConfirming(false);
          setActionError(t('admin.prospection.import.async.failed'));
        }
      } catch (error) {
        if (cancelled) return;
        if (isExpiredError(error)) {
          setPollKind('');
          setExpired(true);
          setBusy(false);
        }
      }
    };
    tick();
    const timer = setInterval(tick, ASYNC_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [importId, pollKind, navigate, t]);

  const mappingCheck = useMemo(
    () =>
      validateMapping(mapping, table?.headers?.length || 0, {
        satisfiedTargets: mappingSatisfiedTargets(importProfile),
      }),
    [mapping, table, importProfile],
  );

  const ownerMailingPreview = useMemo(() => {
    if (ownerAddressMode !== 'composite' || !table?.rows?.length) return '';
    const group = importProfile?.ownerGroups?.[0];
    if (!group) return '';
    const rows = Array.isArray(table.rows) ? table.rows : [];
    const indexes = Object.values(group).filter((value) => Number.isInteger(value));
    const sample =
      rows.find((row) => indexes.some((index) => String(row?.[index] || '').trim())) || rows[0];
    return previewOwnerMailingAddressFromRow(sample, group);
  }, [ownerAddressMode, table, importProfile]);

  const additionalOwnerGroupCount = Math.max(0, (importProfile?.ownerGroups?.length || 0) - 1);
  const hideTopLevelOwnerName = shouldHideTopLevelOwnerNameSelector(importProfile);

  // Hidden JLR owner-name selector must not leave a stale mapping that influences import.
  useEffect(() => {
    if (!hideTopLevelOwnerName) return;
    setMapping((current) => {
      if (!Array.isArray(current) || !current.includes('ownerName')) return current;
      return current.map((target) => (target === 'ownerName' ? IGNORE_TARGET : target));
    });
  }, [hideTopLevelOwnerName, importProfile?.ownerGroups?.[0]?.firstName, importProfile?.ownerGroups?.[0]?.lastName]);

  const columnProfiles = useMemo(
    () => (table ? profileImportColumns(table.headers, table.rows) : []),
    [table],
  );

  const vertexHelpful = vertexMappingHelpful({
    profile: importProfile?.sourceProfile,
    mappingOk: mappingCheck.ok,
    columns: columnProfiles,
    confidence: importProfile?.confidence,
  });

  const previewCounts = useMemo(() => {
    const rows = Array.isArray(preview?.rows) ? preview.rows : [];
    const server = preview?.counts ? countsFromJob({ counts: preview.counts, rowCount: preview.counts?.total }) : null;
    const counts = {
      total: server?.total ?? rows.length,
      ready: server?.ready ?? 0,
      willUpdate: server?.willUpdate ?? 0,
      unchanged: server?.unchanged ?? 0,
      invalid: server?.invalid ?? 0,
      duplicateInFile: server?.duplicateInFile ?? 0,
      ambiguousMatch: server?.ambiguousMatch ?? 0,
      conflict: server?.conflict ?? 0,
      alreadyExists: server?.alreadyExists ?? 0,
      excluded: 0,
      confirmedWillUpdate: 0,
      unresolvedAmbiguous: 0,
    };
    if (!server) {
      for (const row of rows) {
        if (row.status === 'ready') counts.ready += 1;
        else if (row.status === 'will_update') counts.willUpdate += 1;
        else if (row.status === 'unchanged') counts.unchanged += 1;
        else if (row.status === 'invalid') counts.invalid += 1;
        else if (row.status === 'duplicate_in_file') counts.duplicateInFile += 1;
        else if (row.status === 'ambiguous_match') counts.ambiguousMatch += 1;
        else if (row.status === 'conflict') counts.conflict += 1;
        else if (row.status === 'already_exists') counts.alreadyExists += 1;
      }
    }
    for (const row of rows) {
      if (row.status === 'ready' && excluded.has(row.rowNumber)) counts.excluded += 1;
      if (row.status === 'will_update' && confirmedUpdates.has(row.rowNumber)) counts.confirmedWillUpdate += 1;
      if (row.status === 'ambiguous_match' && !matchResolutions[row.rowNumber]) counts.unresolvedAmbiguous += 1;
    }
    counts.selected = Math.max(0, counts.ready - counts.excluded) + counts.confirmedWillUpdate;
    return counts;
  }, [preview, excluded, confirmedUpdates, matchResolutions]);

  const eligibleUpdateRowNumbers = useMemo(
    () => eligibleWillUpdateRowNumbers(preview?.rows),
    [preview],
  );
  const updateSelection = useMemo(
    () => updateSelectionState(confirmedUpdates, eligibleUpdateRowNumbers),
    [confirmedUpdates, eligibleUpdateRowNumbers],
  );
  const selectedEligibleUpdateCount = useMemo(
    () => countConfirmedEligibleUpdates(confirmedUpdates, eligibleUpdateRowNumbers),
    [confirmedUpdates, eligibleUpdateRowNumbers],
  );
  const updatePreviewIsPartialSample = useMemo(
    () => isPartialUpdatePreviewSample(eligibleUpdateRowNumbers.length, previewCounts.willUpdate),
    [eligibleUpdateRowNumbers.length, previewCounts.willUpdate],
  );
  const selectionControlsDisabled = busy || expired || !preview;

  useEffect(() => {
    setConfirmedUpdates((current) => pruneConfirmedUpdates(current, eligibleUpdateRowNumbers));
  }, [eligibleUpdateRowNumbers]);

  useEffect(() => {
    const node = bulkUpdateSelectRef.current;
    if (!node) return;
    node.indeterminate = updateSelection === 'partial';
  }, [updateSelection]);

  const handleBulkUpdateSelectionChange = () => {
    if (selectionControlsDisabled || eligibleUpdateRowNumbers.length === 0) return;
    setConfirmedUpdates(applyBulkUpdateSelectionToggle(updateSelection, eligibleUpdateRowNumbers));
  };


  const parseCurrentSource = (text) => {
    const parsed = parseProspectImportText(text, {
      delimiter,
      hasHeader: null,
      language,
    });
    if (!parsed.ok) {
      setSourceError(t('admin.prospection.import.empty'));
      setTable(null);
      return null;
    }
    if (parsed.rows.length > IMPORT_ROW_LIMIT) {
      setSourceError(t('admin.prospection.import.limit'));
      setTable(null);
      return null;
    }
    setSourceError('');
    setSourceWarnings([]);
    const nextTable = { headers: parsed.headers, rows: parsed.rows };
    const setup = suggestImportSetup(parsed.headers);
    setTable(nextTable);
    setImportProfile(setup.profile);
    setMapping(setup.mapping);
    setOwnerAddressMode(ownerAddressModeFromProfile(setup.profile));
    setVertexSuggestion(null);
    setVertexError('');
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setExecution('sync');
    setDetectedRowCount(parsed.rows.length);
    if (delimiter === 'auto') setDelimiter(parsed.delimiterKey);
    return nextTable;
  };

  const applyParsedTable = (headers, rows, warnings = [], meta = {}) => {
    const setup = suggestImportSetup(headers);
    setTable({ headers, rows });
    setImportProfile(setup.profile);
    setMapping(setup.mapping);
    setOwnerAddressMode(ownerAddressModeFromProfile(setup.profile));
    setVertexSuggestion(null);
    setVertexError('');
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setSourceError('');
    setSourceWarnings(Array.isArray(warnings) ? warnings : []);
    setExecution(meta.execution === 'async' || meta.requiresAsync ? 'async' : 'sync');
    setDetectedRowCount(Number(meta.rowCount) || (Array.isArray(rows) ? rows.length : 0));
  };

  const handleFileSession = async (file, fileType) => {
    if (file.size > ADMIN_PROSPECT_IMPORT_MAX_FILE_BYTES) {
      setSourceError(t('admin.prospection.import.file.tooLarge'));
      return null;
    }
    const contentType = excelContentTypeForFileType(fileType);
    setBusy(true);
    setFilePhase('uploading');
    setSourceError('');
    setSourceWarnings([]);
    const previousId = importId;
    try {
      if (previousId) {
        await discardPreview(previousId);
        setImportId(null);
        setPreview(null);
      }
      const result = await runExcelImportSession({
        createUpload: (payload) =>
          createAdminProspectImportUpload(buildCreateAdminProspectImportUploadRequest(payload)),
        uploadSource: uploadProspectImportSource,
        parseFile: (payload) =>
          parseAdminProspectImportFile(
            buildParseAdminProspectImportFileRequest({
              ...payload,
              delimiter: fileType === 'csv' ? delimiter : undefined,
              hasHeader: fileType === 'csv' ? hasHeaderFromMode(headerMode) : undefined,
            }),
          ),
        cancelImport: (payload) => cancelAdminProspectImport(payload),
        onImportId: (id) => setImportId(id),
        onPhase: (phase) => setFilePhase(phase),
        file,
        fileType,
        contentType,
        byteSize: file.size,
        parseOptions:
          fileType === 'csv'
            ? { delimiter, hasHeader: hasHeaderFromMode(headerMode) }
            : undefined,
      });
      const parsed = result.parsed;
      const rows = parsed.requiresAsync || parsed.execution === 'async' ? parsed.sampleRows || [] : parsed.rows;
      applyParsedTable(parsed.headers, rows, parsed.warnings, parsed);
      setInputMode(fileType === 'csv' ? 'file_csv' : 'file_excel');
      setFilePhase('ready');
      return parsed;
    } catch (error) {
      setImportId(null);
      setTable(null);
      setFilePhase('');
      setSourceError(t(excelErrorKeyFor(error)));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const fileType = importFileTypeFromName(file.name);
    if (!fileType) {
      setFileName('');
      setSelectedFile(null);
      setSelectedFileType(null);
      setSourceError(t('admin.prospection.import.csv.invalidType'));
      return;
    }
    setFileName(file.name);
    setSelectedFile(file);
    setSelectedFileType(fileType);
    setTable(null);
    setFilePhase('');
    setSourceWarnings([]);
    setSourceError('');
    setVertexSuggestion(null);
    setVertexError('');
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setOwnerAddressMode('single');
    setInputMode(fileType === 'csv' ? 'file_csv' : 'file_excel');
  };

  const goToMapping = async () => {
    if (inputMode === 'paste') {
      const parsed = parseCurrentSource(pasteText);
      if (!parsed) return;
      setStep('mapping');
      return;
    }
    if (table) {
      setStep('mapping');
      return;
    }
    if (!selectedFile || !selectedFileType) {
      setSourceError(t('admin.prospection.import.file.needed'));
      return;
    }
    const parsed = await handleFileSession(selectedFile, selectedFileType);
    if (!parsed) return;
    setStep('mapping');
  };

  const handleMappingChange = (index, target) => {
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setMapping((current) => {
      const next = [...current];
      const value = target === IGNORE_TARGET ? IGNORE_TARGET : target;
      if (value && value !== IGNORE_TARGET) {
        const previous = next.findIndex((item, itemIndex) => itemIndex !== index && item === value);
        if (previous >= 0) next[previous] = IGNORE_TARGET;
      }
      next[index] = value;
      setImportProfile((profile) => applyReviewedMappingToProfile(profile, next, next.length));
      return next;
    });
  };

  const assignMappingTarget = (columnIndex, target) => {
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setMapping((current) => {
      const next = [...current];
      for (let i = 0; i < next.length; i += 1) {
        if (next[i] === target) next[i] = IGNORE_TARGET;
      }
      if (Number.isInteger(columnIndex) && columnIndex >= 0 && columnIndex < next.length) {
        next[columnIndex] = target;
      }
      setImportProfile((profile) => applyReviewedMappingToProfile(profile, next, next.length));
      return next;
    });
  };

  const handleOwnerAddressModeChange = (mode) => {
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    const next = applyOwnerAddressMode(importProfile, mapping, mode);
    const profile = applyReviewedMappingToProfile(next.profile, next.mapping, next.mapping.length);
    setImportProfile(profile);
    setMapping(next.mapping);
    setOwnerAddressMode(mode);
  };

  const handlePrimaryOwnerSlotChange = (slot, index) => {
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setImportProfile((current) => {
      const updated = setPrimaryOwnerGroupSlot(current, slot, index);
      return applyReviewedMappingToProfile(updated, mapping, mapping.length);
    });
  };

  const goToPreview = async () => {
    if (!table || !mappingCheck.ok) return;
    setBusy(true);
    setActionError('');
    try {
      const reviewedProfile = applyReviewedMappingToProfile(importProfile, mapping, mapping.length);
      setImportProfile(reviewedProfile);
      if (execution === 'async' && importId) {
        await confirmAdminProspectImportMapping(
          buildConfirmAdminProspectImportMappingRequest({
            importId,
            mapping,
            profile: reviewedProfile,
          }),
        );
        setPreview(null);
        setExcluded(new Set());
        setExpired(false);
        setStep('preview');
        setPollKind('preview');
        return;
      }
      const rows = mapRowsWithProfile(table.rows, mapping, reviewedProfile);
      const previousId = importId;
      if (!isFileInputMode(inputMode) && previousId) {
        await discardPreview(previousId);
        setImportId(null);
        setPreview(null);
      }
      const data = await previewAdminProspectImport(
        buildPreviewAdminProspectImportRequest({
          inputMode,
          rows,
          importId: isFileInputMode(inputMode) ? importId : undefined,
        }),
      );
      setPreview(data);
      setImportId(data.importId);
      setExcluded(new Set());
      setConfirmedUpdates(new Set());
      setMatchResolutions({});
      setExpired(false);
      setStep('preview');
      setBusy(false);
    } catch (error) {
      setActionError(t(errorKeyFor(error)));
      setBusy(false);
    }
  };

  const handleVertexSuggest = async () => {
    if (!table || vertexBusy || busy) return;
    setVertexBusy(true);
    setVertexError('');
    setVertexSuggestion(null);
    setVertexApplyFeedback(null);
    try {
      const data = await suggestAdminProspectImportMapping(
        buildSuggestAdminProspectImportMappingRequest({
          sourceProfileCandidate: importProfile?.sourceProfile === 'jlr' ? 'jlr' : 'generic',
          columns: columnProfiles,
        }),
      );
      if (!data?.ok || !data.suggestion) {
        setVertexError(t('admin.prospection.import.vertex.unavailable'));
        return;
      }
      setVertexSuggestion(data.suggestion);
    } catch {
      setVertexError(t('admin.prospection.import.vertex.unavailable'));
    } finally {
      setVertexBusy(false);
    }
  };

  const handleApplyVertexSuggestion = () => {
    if (!vertexSuggestion || !table) return;
    const next = applyVertexSuggestionToSetup(
      vertexSuggestion,
      table.headers,
      importProfile,
      mapping,
    );
    setMapping(next.mapping);
    setImportProfile(next.profile);
    setOwnerAddressMode(ownerAddressModeFromProfile(next.profile));
    setRestoreJlrFeedback(false);
    setVertexApplyFeedback({
      kind: (next.filledCount || 0) > 0 ? 'changed' : 'unchanged',
      count: next.filledCount || 0,
    });
  };

  const handleRestoreJlrMapping = () => {
    if (!table || importProfile?.sourceProfile !== 'jlr') return;
    const setup = suggestImportSetup(table.headers);
    setMapping(setup.mapping);
    setImportProfile(setup.profile);
    setOwnerAddressMode(ownerAddressModeFromProfile(setup.profile));
    setVertexSuggestion(null);
    setVertexApplyFeedback(null);
    setVertexError('');
    setRestoreJlrFeedback(true);
  };

  const toggleInclude = (row) => {
    if (selectionControlsDisabled) return;
    if (row.status === 'ready') {
      setExcluded((current) => {
        const next = new Set(current);
        if (next.has(row.rowNumber)) next.delete(row.rowNumber);
        else next.add(row.rowNumber);
        return next;
      });
      return;
    }
    if (row.status === 'will_update') {
      setConfirmedUpdates((current) => {
        const next = new Set(current);
        if (next.has(row.rowNumber)) next.delete(row.rowNumber);
        else next.add(row.rowNumber);
        return next;
      });
    }
  };

  const setAmbiguousResolution = (rowNumber, resolution) => {
    setMatchResolutions((current) => ({
      ...current,
      [rowNumber]: resolution,
    }));
  };

  const discardPreview = async (id = importId) => {
    if (!id) return;
    try {
      await cancelAdminProspectImport({ importId: id });
    } catch {
      /* best-effort; abandoned browser sessions are swept hourly after 24h */
    }
  };

  const leavePreviewStep = async (nextStep) => {
    setBusy(true);
    setPollKind('');
    await discardPreview();
    setPreview(null);
    setImportId(null);
    setExcluded(new Set());
    setConfirmedUpdates(new Set());
    setMatchResolutions({});
    setStep(nextStep);
    setBusy(false);
  };

  const leavePreviewToMapping = async () => {
    if (isFileInputMode(inputMode) && importId) {
      setPreview(null);
      setExcluded(new Set());
      setConfirmedUpdates(new Set());
      setMatchResolutions({});
      setPollKind('');
      setStep('mapping');
      return;
    }
    await leavePreviewStep('mapping');
  };

  const leaveMappingToSource = async () => {
    setBusy(true);
    setPollKind('');
    await discardPreview();
    setPreview(null);
    setImportId(null);
    setExcluded(new Set());
    setConfirmedUpdates(new Set());
    setMatchResolutions({});
    if (isFileInputMode(inputMode)) {
      setTable(null);
      setMapping([]);
      setImportProfile(null);
      setFilePhase('');
      setSourceWarnings([]);
      setVertexSuggestion(null);
      setVertexError('');
      setVertexApplyFeedback(null);
      setRestoreJlrFeedback(false);
      setOwnerAddressMode('single');
    }
    setStep('source');
    setBusy(false);
  };

  const handleCancel = async () => {
    setPollKind('');
    await discardPreview();
    setImportId(null);
    setPreview(null);
    setExcluded(new Set());
    setConfirmedUpdates(new Set());
    setMatchResolutions({});
    navigate('/admin/prospection');
  };

  const handleRestart = async () => {
    setPollKind('');
    await discardPreview();
    setStep('source');
    setPreview(null);
    setImportId(null);
    setExcluded(new Set());
    setConfirmedUpdates(new Set());
    setMatchResolutions({});
    setExpired(false);
    setActionError('');
    setConfirming(false);
    setHeaderMode('auto');
    setImportProfile(null);
    setVertexSuggestion(null);
    setVertexError('');
    setVertexApplyFeedback(null);
    setRestoreJlrFeedback(false);
    setOwnerAddressMode('single');
    setFilePhase('');
    setSourceWarnings([]);
    setFileName('');
    setSelectedFile(null);
    setSelectedFileType(null);
    setJobProgress(null);
    setExecution('sync');
  };

  const handleCommit = async () => {
    if (!importId || previewCounts.selected <= 0 || busy || expired || previewCounts.unresolvedAmbiguous > 0) {
      return;
    }
    const confirmed = window.confirm(t('admin.prospection.import.confirmDialog'));
    if (!confirmed) return;
    setBusy(true);
    setConfirming(true);
    setActionError('');
    const resolutionPayload = Object.entries(matchResolutions).map(([rowNumber, resolution]) => {
      const row = Number(rowNumber);
      if (resolution?.skip) return { rowNumber: row, skip: true };
      return { rowNumber: row, prospectId: String(resolution?.prospectId || '').trim() };
    });
    try {
      const result = await commitAdminProspectImport(
        buildCommitAdminProspectImportRequest({
          importId,
          excludedRowNumbers: [...excluded],
          confirmedUpdateRowNumbers: [...pruneConfirmedUpdates(confirmedUpdates, eligibleUpdateRowNumbers)],
          matchResolutions: resolutionPayload,
        }),
      );
      if (result?.status === 'importing' || execution === 'async') {
        setPollKind('import');
        return;
      }
      navigate('/admin/prospection', {
        state: {
          importResult: {
            inserted: Number(result?.inserted) || 0,
            skippedDuplicate: Number(result?.skippedDuplicate) || 0,
            failed: Number(result?.failed) || 0,
          },
        },
      });
    } catch (error) {
      if (isExpiredError(error)) {
        setExpired(true);
      } else {
        setActionError(t(errorKeyFor(error)));
      }
      setConfirming(false);
      setBusy(false);
    }
  };

  const stepIndex = STEPS.indexOf(step);
  const showCsvOptions = inputMode !== 'paste' && selectedFileType === 'csv';
  const analyzing = pollKind === 'preview';
  const importingAsync = pollKind === 'import';

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-slate-500">
            <Link
              to="/admin/prospection"
              onClick={(event) => {
                event.preventDefault();
                handleCancel();
              }}
              className="font-medium text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.title')}
            </Link>
            <span aria-hidden="true"> / </span>
            <span>{t('admin.prospection.import.title')}</span>
          </p>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-1 text-xl font-semibold text-slate-900 focus:outline-none"
          >
            {t('admin.prospection.import.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.import.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('admin.prospection.import.cancel')}
        </button>
      </div>

      <ol className="flex flex-wrap gap-2 text-sm" aria-label={t('admin.prospection.import.steps')}>
        {STEP_LABELS.map((item, index) => {
          const current = item === step || (step === 'preview' && item === 'import' && confirming);
          const reached = STEPS.indexOf(item) >= 0 ? STEPS.indexOf(item) <= stepIndex : step === 'preview';
          return (
            <li
              key={item}
              aria-current={current ? 'step' : undefined}
              className={`rounded-full px-3 py-1 ${
                current ? 'bg-indigo-600 text-white' : reached ? 'bg-indigo-50 text-indigo-800' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {index + 1}. {t(`admin.prospection.import.step.${item}`)}
            </li>
          );
        })}
      </ol>

      {expired ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          <p>{t('admin.prospection.import.expired')}</p>
          <button
            type="button"
            onClick={handleRestart}
            className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.import.restart')}
          </button>
        </div>
      ) : null}

      {actionError ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
          {actionError}
        </p>
      ) : null}

      {step === 'source' && !expired ? (
        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-slate-800">{t('admin.prospection.import.mode')}</legend>
            <div className="flex flex-col gap-2 sm:flex-row">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="admin-prospect-import-mode"
                  checked={inputMode === 'paste'}
                  onChange={() => {
                    if (isFileInputMode(inputMode) && importId) {
                      discardPreview(importId);
                      setImportId(null);
                    }
                    setInputMode('paste');
                    setFilePhase('');
                    setSelectedFile(null);
                    setSelectedFileType(null);
                    setFileName('');
                    setTable(null);
                    setSourceError('');
                  }}
                />
                {t('admin.prospection.import.mode.paste')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="admin-prospect-import-mode"
                  checked={inputMode !== 'paste'}
                  onChange={() => {
                    if (inputMode === 'paste' && importId) {
                      discardPreview(importId);
                      setImportId(null);
                    }
                    setInputMode('file_csv');
                    setFilePhase('');
                    setPasteText('');
                    setTable(null);
                    setSourceError('');
                  }}
                />
                {t('admin.prospection.import.mode.csv')}
              </label>
            </div>
          </fieldset>

          {inputMode === 'paste' ? (
            <label className="block text-sm" htmlFor={pasteId}>
              <span className="mb-1 block font-medium text-slate-700">{t('admin.prospection.import.paste.label')}</span>
              <textarea
                id={pasteId}
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                rows={12}
                spellCheck="false"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={t('admin.prospection.import.paste.hint')}
              />
            </label>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor={fileId}>
                {t('admin.prospection.import.csv.label')}
              </label>
              <p className="mt-1 text-sm text-slate-500">{t('admin.prospection.import.csv.hint')}</p>
              <input
                id={fileId}
                type="file"
                accept={IMPORT_FILE_ACCEPT}
                onChange={handleFile}
                disabled={busy}
                className="mt-2 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700"
              />
              {fileName ? <p className="mt-1 text-xs text-slate-500">{fileName}</p> : null}
              {filePhase === 'uploading' ? (
                <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.import.file.uploading')}</p>
              ) : null}
              {filePhase === 'parsing' ? (
                <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.import.file.parsing')}</p>
              ) : null}
              {filePhase === 'ready' ? (
                <p className="mt-1 text-sm text-emerald-800">{t('admin.prospection.import.file.ready')}</p>
              ) : null}
            </div>
          )}

          {showCsvOptions ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm" htmlFor={delimiterId}>
                <span className="mb-1 block text-slate-600">{t('admin.prospection.import.delimiter')}</span>
                <select
                  id={delimiterId}
                  value={delimiter}
                  onChange={(event) => setDelimiter(event.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {DELIMITER_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`admin.prospection.import.delimiter.${option}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm" htmlFor={headerId}>
                <span className="mb-1 block text-slate-600">{t('admin.prospection.import.headerMode')}</span>
                <select
                  id={headerId}
                  value={headerMode}
                  onChange={(event) => setHeaderMode(event.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {HEADER_MODE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`admin.prospection.import.headerMode.${option}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {sourceWarnings.includes('multiple_sheets_first_used') ? (
            <p className="text-sm text-amber-800" role="status">
              {t('admin.prospection.import.warning.multiple_sheets_first_used')}
            </p>
          ) : null}

          {execution === 'async' && detectedRowCount > IMPORT_ROW_LIMIT ? (
            <p className="text-sm text-indigo-800" role="status">
              {t('admin.prospection.import.async.info').replace('{count}', String(detectedRowCount))}
            </p>
          ) : null}

          {sourceError ? (
            <p className="text-sm text-red-700" role="alert">
              {sourceError}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={goToMapping}
              disabled={busy || filePhase === 'uploading' || filePhase === 'parsing'}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.import.continue')}
            </button>
          </div>
        </section>
      ) : null}

      {step === 'mapping' && table && !expired ? (
        <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-600">{t('admin.prospection.import.mapping.help')}</p>
          {execution === 'async' && detectedRowCount > IMPORT_ROW_LIMIT ? (
            <p className="text-sm text-indigo-800" role="status">
              {t('admin.prospection.import.async.info').replace('{count}', String(detectedRowCount))}
            </p>
          ) : null}
          {importProfile?.sourceProfile === 'jlr' ? (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-950">
              <p className="font-semibold">{t('admin.prospection.import.profile.jlr')}</p>
              <p className="mt-1">
                {t('admin.prospection.import.profile.confidence').replace(
                  '{value}',
                  String(Math.round((importProfile.confidence || 0) * 100)),
                )}
              </p>
              <p className="mt-1">{t('admin.prospection.import.profile.jlrAuto')}</p>
              <p className="mt-1">{t('admin.prospection.import.profile.provinceQc')}</p>
              <p className="mt-1">
                {t('admin.prospection.import.profile.owners').replace(
                  '{count}',
                  String(importProfile.ownerGroups?.length || 0),
                )}
              </p>
              <button
                type="button"
                onClick={handleRestoreJlrMapping}
                disabled={busy || vertexBusy}
                className="mt-3 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              >
                {t('admin.prospection.import.profile.restoreJlr')}
              </button>
              {restoreJlrFeedback ? (
                <p className="mt-2" role="status" aria-live="polite">
                  {t('admin.prospection.import.profile.restoreJlr.done')}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-600">{t('admin.prospection.import.profile.generic')}</p>
          )}
          {sourceWarnings.includes('multiple_sheets_first_used') ? (
            <p className="text-sm text-amber-800" role="status">
              {t('admin.prospection.import.warning.multiple_sheets_first_used')}
            </p>
          ) : null}
          {!mappingCheck.ok ? (
            <p className="text-sm text-red-700" role="alert">
              {t('admin.prospection.import.mapping.required')}
            </p>
          ) : null}

          <div className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">{t('admin.prospection.import.vertex.help')}</p>
            <button
              type="button"
              onClick={handleVertexSuggest}
              disabled={vertexBusy || busy}
              className={`rounded-lg px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 ${
                vertexHelpful
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {vertexBusy
                ? t('admin.prospection.import.vertex.analyzing')
                : t('admin.prospection.import.vertex.button')}
            </button>
            {vertexError ? (
              <p className="text-sm text-amber-800" role="status">
                {vertexError}
              </p>
            ) : null}
            {vertexSuggestion ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
                <p className="font-semibold">{t('admin.prospection.import.vertex.suggestion')}</p>
                <p className="mt-1">
                  {t('admin.prospection.import.profile.confidence').replace(
                    '{value}',
                    String(Math.round((vertexSuggestion.confidence || 0) * 100)),
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleApplyVertexSuggestion}
                  className="mt-2 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {t('admin.prospection.import.vertex.apply')}
                </button>
                {vertexApplyFeedback ? (
                  <p className="mt-2" role="status" aria-live="polite">
                    {vertexApplyFeedback.kind === 'changed'
                      ? t('admin.prospection.import.vertex.apply.changed').replace(
                          '{count}',
                          String(vertexApplyFeedback.count),
                        )
                      : t('admin.prospection.import.vertex.apply.unchanged')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 p-3">
            <h2 className="text-sm font-semibold text-slate-800">
              {t('admin.prospection.import.owner.section')}
            </h2>
            {importProfile?.sourceProfile === 'jlr' ? (
              <p className="text-sm text-slate-600">{t('admin.prospection.import.owner.jlrComposed')}</p>
            ) : null}
            {hideTopLevelOwnerName ? (
              <p className="text-sm text-slate-600" role="status">
                {t('admin.prospection.import.owner.name.composedFromSplit')}
              </p>
            ) : (
              <OwnerColumnSelect
                id="import-owner-name"
                label={t('admin.prospection.import.owner.name')}
                headers={table.headers}
                value={columnIndexForTarget(mapping, 'ownerName')}
                onChange={(index) => assignMappingTarget(index, 'ownerName')}
                noneLabel={t('admin.prospection.import.owner.column.none')}
              />
            )}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-800">
                {t('admin.prospection.import.owner.address')}
              </legend>
              <p className="text-sm text-slate-600">{t('admin.prospection.import.owner.mode')}</p>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="owner-address-mode"
                  checked={ownerAddressMode === 'single'}
                  onChange={() => handleOwnerAddressModeChange('single')}
                />
                {t('admin.prospection.import.owner.mode.single')}
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="owner-address-mode"
                  checked={ownerAddressMode === 'composite'}
                  onChange={() => handleOwnerAddressModeChange('composite')}
                />
                {t('admin.prospection.import.owner.mode.composite')}
              </label>
            </fieldset>
            {ownerAddressMode === 'single' ? (
              <OwnerColumnSelect
                id="import-owner-mailing"
                label={t('admin.prospection.import.owner.address')}
                headers={table.headers}
                value={columnIndexForTarget(mapping, 'ownerMailingAddress')}
                onChange={(index) => assignMappingTarget(index, 'ownerMailingAddress')}
                noneLabel={t('admin.prospection.import.owner.column.none')}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {PRIMARY_OWNER_COMPOSITE_SLOTS.map((item) => (
                  <OwnerColumnSelect
                    key={item.slot}
                    id={`import-owner-slot-${item.slot}`}
                    label={t(item.key)}
                    headers={table.headers}
                    value={importProfile?.ownerGroups?.[0]?.[item.slot]}
                    onChange={(index) => handlePrimaryOwnerSlotChange(item.slot, index)}
                    noneLabel={t('admin.prospection.import.owner.column.none')}
                  />
                ))}
              </div>
            )}
            {ownerAddressMode === 'composite' && ownerMailingPreview ? (
              <p className="text-sm text-slate-700" role="status">
                {t('admin.prospection.import.owner.preview').replace('{address}', ownerMailingPreview)}
              </p>
            ) : null}
            {additionalOwnerGroupCount > 0 ? (
              <p className="text-sm text-slate-600">
                {t('admin.prospection.import.owner.additionalGroups').replace(
                  '{count}',
                  String(additionalOwnerGroupCount),
                )}
              </p>
            ) : null}
          </div>

          <div className="hidden overflow-hidden rounded-lg border border-slate-200 md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.prospection.import.step.mapping')}</caption>
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.import.mapping.fileColumn')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.import.mapping.target')}</th>
                </tr>
              </thead>
              <tbody>
                {table.headers.map((header, index) => (
                  <tr key={`${header}-${index}`} className="border-t border-slate-100">
                    <th scope="row" className="px-3 py-2 font-medium text-slate-800">
                      {header}
                    </th>
                    <td className="px-3 py-2">
                      <label className="sr-only" htmlFor={`import-map-${index}`}>
                        {header}
                      </label>
                      <select
                        id={`import-map-${index}`}
                        value={PROPERTY_MAPPING_TARGETS.includes(mapping[index]) ? mapping[index] : IGNORE_TARGET}
                        onChange={(event) => handleMappingChange(index, event.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={IGNORE_TARGET}>{t('admin.prospection.import.mapping.ignore')}</option>
                        {PROPERTY_MAPPING_TARGETS.map((target) => (
                          <option key={target} value={target}>
                            {t(`admin.prospection.import.target.${target}`)}
                            {REQUIRED_IMPORT_TARGETS.includes(target) ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {table.headers.map((header, index) => (
              <div key={`${header}-${index}`} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-medium text-slate-800">{header}</p>
                <label className="mt-2 block text-sm" htmlFor={`import-map-m-${index}`}>
                  <span className="mb-1 block text-slate-600">{t('admin.prospection.import.mapping.target')}</span>
                  <select
                    id={`import-map-m-${index}`}
                    value={PROPERTY_MAPPING_TARGETS.includes(mapping[index]) ? mapping[index] : IGNORE_TARGET}
                    onChange={(event) => handleMappingChange(index, event.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={IGNORE_TARGET}>{t('admin.prospection.import.mapping.ignore')}</option>
                    {PROPERTY_MAPPING_TARGETS.map((target) => (
                      <option key={target} value={target}>
                        {t(`admin.prospection.import.target.${target}`)}
                        {REQUIRED_IMPORT_TARGETS.includes(target) ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                if (importId) {
                  leaveMappingToSource();
                  return;
                }
                setStep('source');
              }}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.import.back')}
            </button>
            <button
              type="button"
              onClick={goToPreview}
              disabled={!mappingCheck.ok || busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {busy ? t('admin.prospection.import.previewing') : t('admin.prospection.import.continue')}
            </button>
          </div>
        </section>
      ) : null}

      {step === 'preview' && !expired ? (
        <section className="space-y-4">
          {analyzing || importingAsync ? (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950" role="status">
              <p>
                {analyzing
                  ? jobProgress?.previewPhase === 'duplicate_check'
                    ? t('admin.prospection.import.async.phase.duplicateCheck')
                    : jobProgress?.previewPhase === 'finalizing'
                      ? t('admin.prospection.import.async.phase.finalizing')
                      : t('admin.prospection.import.async.analyzing').replace(
                          '{count}',
                          String(detectedRowCount || jobProgress?.rowCount || 0),
                        )
                  : t('admin.prospection.import.confirming')}
              </p>
              {analyzing &&
              (jobProgress?.previewPhase === 'validating' || !jobProgress?.previewPhase) &&
              Number(jobProgress?.processedRows) > 0 ? (
                <p className="mt-1">
                  {t('admin.prospection.import.async.progress')
                    .replace('{processed}', String(jobProgress.processedRows))
                    .replace('{total}', String(jobProgress.rowCount || detectedRowCount || 0))}
                </p>
              ) : null}
            </div>
          ) : null}

          {preview ? (
            <>
          <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3 lg:grid-cols-5">
            {[
              ['total', previewCounts.total],
              ['ready', previewCounts.ready],
              ['willUpdate', previewCounts.willUpdate],
              ['unchanged', previewCounts.unchanged],
              ['invalid', previewCounts.invalid],
              ['duplicateInFile', previewCounts.duplicateInFile],
              ['ambiguousMatch', previewCounts.ambiguousMatch],
              ['conflict', previewCounts.conflict],
              ['alreadyExists', previewCounts.alreadyExists],
              ['excluded', previewCounts.excluded],
            ].map(([key, value]) => (
              <div key={key} className="rounded-lg border border-slate-200 bg-white p-3">
                <dt className="text-xs uppercase tracking-wide text-slate-500">
                  {t(`admin.prospection.import.counts.${key}`)}
                </dt>
                <dd className="mt-1 text-lg font-semibold text-slate-900">{value}</dd>
              </div>
            ))}
          </dl>

          {execution === 'async' ? (
            <p className="text-sm text-slate-600">{t('admin.prospection.import.exclude.sample')}</p>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
            <label className="flex items-start gap-3 text-sm text-slate-800">
              <input
                ref={bulkUpdateSelectRef}
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                checked={updateSelection === 'all' && eligibleUpdateRowNumbers.length > 0}
                disabled={selectionControlsDisabled || eligibleUpdateRowNumbers.length === 0}
                onChange={handleBulkUpdateSelectionChange}
                aria-checked={updateSelection === 'partial' ? 'mixed' : updateSelection === 'all'}
              />
              <span className="min-w-0 space-y-1">
                <span className="block font-medium">
                  {updatePreviewIsPartialSample
                    ? t('admin.prospection.import.selectUpdatesInPreview')
                    : t('admin.prospection.import.selectAllUpdates')}
                </span>
                <span className="block text-slate-600">
                  {updatePreviewIsPartialSample
                    ? t('admin.prospection.import.updateSelection.partialSampleCount')
                        .replace('{selected}', String(selectedEligibleUpdateCount))
                        .replace('{available}', String(eligibleUpdateRowNumbers.length))
                        .replace('{total}', String(previewCounts.willUpdate))
                    : t('admin.prospection.import.updateSelection.count')
                        .replace('{selected}', String(selectedEligibleUpdateCount))
                        .replace('{total}', String(eligibleUpdateRowNumbers.length))}
                </span>
                {updatePreviewIsPartialSample ? (
                  <span className="block text-slate-600">
                    {t('admin.prospection.import.updateSelection.partialSampleHint')}
                  </span>
                ) : null}
              </span>
            </label>
          </div>

          <div className="hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.prospection.import.step.preview')}</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.import.col.row')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.field.address')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.field.city')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.field.province')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.field.postalCode')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.col.units')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.col.owner')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.col.status')}</th>
                  <th scope="col" className="px-3 py-2">{t('admin.prospection.import.col.include')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const included =
                    (row.status === 'ready' && !excluded.has(row.rowNumber)) ||
                    (row.status === 'will_update' && confirmedUpdates.has(row.rowNumber));
                  const displayStatus =
                    row.status === 'ready' && excluded.has(row.rowNumber)
                      ? 'skipped'
                      : row.status === 'will_update' && !confirmedUpdates.has(row.rowNumber)
                        ? 'will_update'
                        : row.status;
                  const changeSummary = formatImportPreviewChangeSummary(row, t, language);
                  const resolution = matchResolutions[row.rowNumber];
                  return (
                    <tr key={row.rowNumber} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">{row.rowNumber}</td>
                      <td className="px-3 py-2">{row.address || '—'}</td>
                      <td className="px-3 py-2">{row.city || '—'}</td>
                      <td className="px-3 py-2">{row.province || '—'}</td>
                      <td className="px-3 py-2">{row.postalCode || '—'}</td>
                      <td className="px-3 py-2">{row.numberOfUnits ?? '—'}</td>
                      <td className="px-3 py-2">
                        {row.ownerName || '—'}
                        {row.ownerCount > 1 ? (
                          <span className="ml-1 text-xs text-slate-500">
                            ({t('admin.prospection.import.owners.count').replace('{count}', String(row.ownerCount))})
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <span>{t(rowStatusKey(displayStatus))}</span>
                        {changeSummary ? (
                          <div className="mt-1 space-y-0.5 text-xs text-slate-600 whitespace-pre-line">
                            {changeSummary}
                          </div>
                        ) : null}
                        {row.status === 'invalid' && Array.isArray(row.errors)
                          ? row.errors.map((err) => (
                              <p key={`${err.field}-${err.code}`} className="text-xs text-red-700">
                                {t(fieldErrorKey(err.field, err.code))}
                              </p>
                            ))
                          : null}
                        {Array.isArray(row.warnings) && row.warnings.includes('property_address_mismatch') ? (
                          <p className="text-xs text-amber-800">
                            {t('admin.prospection.import.warning.property_address_mismatch')}
                          </p>
                        ) : null}
                        {row.status === 'ambiguous_match' && Array.isArray(row.candidates) ? (
                          <fieldset className="mt-2 space-y-1">
                            <legend className="text-xs font-medium text-slate-700">
                              {t('admin.prospection.import.resolveAmbiguous').replace('{row}', String(row.rowNumber))}
                            </legend>
                            {row.candidates.map((candidate) => (
                              <label key={candidate.id} className="flex items-center gap-2 text-xs text-slate-800">
                                <input
                                  type="radio"
                                  name={`ambiguous-${row.rowNumber}`}
                                  checked={resolution?.prospectId === candidate.id}
                                  onChange={() => setAmbiguousResolution(row.rowNumber, { prospectId: candidate.id })}
                                />
                                <span>
                                  {[candidate.address, candidate.city, candidate.postalCode].filter(Boolean).join(', ')}
                                </span>
                              </label>
                            ))}
                            <label className="flex items-center gap-2 text-xs text-slate-800">
                              <input
                                type="radio"
                                name={`ambiguous-${row.rowNumber}`}
                                checked={resolution?.skip === true}
                                onChange={() => setAmbiguousResolution(row.rowNumber, { skip: true })}
                              />
                              <span>{t('admin.prospection.import.resolveSkip')}</span>
                            </label>
                          </fieldset>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        {row.status === 'ready' || row.status === 'will_update' ? (
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={included}
                              disabled={selectionControlsDisabled}
                              onChange={() => toggleInclude(row)}
                              aria-label={
                                row.status === 'will_update'
                                  ? t('admin.prospection.import.confirmUpdate').replace('{row}', String(row.rowNumber))
                                  : t('admin.prospection.import.col.include')
                              }
                            />
                            <span className="sr-only">{t('admin.prospection.import.col.include')}</span>
                          </label>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {preview.rows.map((row) => {
              const included =
                (row.status === 'ready' && !excluded.has(row.rowNumber)) ||
                (row.status === 'will_update' && confirmedUpdates.has(row.rowNumber));
              const displayStatus =
                row.status === 'ready' && excluded.has(row.rowNumber)
                  ? 'skipped'
                  : row.status === 'will_update' && !confirmedUpdates.has(row.rowNumber)
                    ? 'will_update'
                    : row.status;
              const changeSummary = formatImportPreviewChangeSummary(row, t, language);
              const resolution = matchResolutions[row.rowNumber];
              const includeDisabled =
                selectionControlsDisabled ||
                (row.status !== 'ready' && row.status !== 'will_update');
              return (
                <article key={row.rowNumber} className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
                  <p className="font-semibold text-slate-900">
                    {t('admin.prospection.import.col.row')} {row.rowNumber}
                  </p>
                  <p>{row.address}</p>
                  <p className="text-slate-600">
                    {row.city} {row.province} {row.postalCode}
                  </p>
                  <p>{t(rowStatusKey(displayStatus))}</p>
                  {changeSummary ? (
                    <div className="mt-1 space-y-0.5 text-xs text-slate-600 whitespace-pre-line">{changeSummary}</div>
                  ) : null}
                  {row.status === 'invalid' && Array.isArray(row.errors)
                    ? row.errors.map((err) => (
                        <p key={`${err.field}-${err.code}`} className="text-red-700">
                          {t(fieldErrorKey(err.field, err.code))}
                        </p>
                      ))
                    : null}
                  {Array.isArray(row.warnings) && row.warnings.includes('property_address_mismatch') ? (
                    <p className="text-xs text-amber-800">
                      {t('admin.prospection.import.warning.property_address_mismatch')}
                    </p>
                  ) : null}
                  {row.status === 'ambiguous_match' && Array.isArray(row.candidates) ? (
                    <fieldset className="mt-2 space-y-1">
                      <legend className="text-xs font-medium text-slate-700">
                        {t('admin.prospection.import.resolveAmbiguous').replace('{row}', String(row.rowNumber))}
                      </legend>
                      {row.candidates.map((candidate) => (
                        <label key={candidate.id} className="flex items-center gap-2 text-xs text-slate-800">
                          <input
                            type="radio"
                            name={`ambiguous-mobile-${row.rowNumber}`}
                            checked={resolution?.prospectId === candidate.id}
                            onChange={() => setAmbiguousResolution(row.rowNumber, { prospectId: candidate.id })}
                          />
                          <span>
                            {[candidate.address, candidate.city, candidate.postalCode].filter(Boolean).join(', ')}
                          </span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 text-xs text-slate-800">
                        <input
                          type="radio"
                          name={`ambiguous-mobile-${row.rowNumber}`}
                          checked={resolution?.skip === true}
                          onChange={() => setAmbiguousResolution(row.rowNumber, { skip: true })}
                        />
                        <span>{t('admin.prospection.import.resolveSkip')}</span>
                      </label>
                    </fieldset>
                  ) : null}
                  {row.status === 'ready' || row.status === 'will_update' ? (
                    <label className="mt-2 inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={included}
                        disabled={includeDisabled}
                        onChange={() => toggleInclude(row)}
                        aria-label={
                          row.status === 'will_update'
                            ? t('admin.prospection.import.confirmUpdate').replace('{row}', String(row.rowNumber))
                            : t('admin.prospection.import.col.include')
                        }
                      />
                      {row.status === 'will_update'
                        ? t('admin.prospection.import.confirmUpdate').replace('{row}', String(row.rowNumber))
                        : t('admin.prospection.import.col.include')}
                    </label>
                  ) : null}
                </article>
              );
            })}
          </div>

          {previewCounts.unresolvedAmbiguous > 0 ? (
            <p className="text-sm text-amber-800" role="alert">
              {t('admin.prospection.import.commitBlockedAmbiguous')}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => leavePreviewToMapping()}
              disabled={busy}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.import.back')}
            </button>
            <button
              type="button"
              onClick={handleCommit}
              disabled={previewCounts.selected <= 0 || busy || expired || previewCounts.unresolvedAmbiguous > 0}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {confirming
                ? t('admin.prospection.import.confirming')
                : t('admin.prospection.import.confirm').replace('{count}', String(previewCounts.selected))}
            </button>
          </div>
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function excelErrorKeyFor(error) {
  const details = callableDetails(error);
  const reason = details && typeof details === 'object' ? details.reason : null;
  if (reason === 'file_too_large') return 'admin.prospection.import.file.tooLarge';
  if (reason === 'too_many_rows') return 'admin.prospection.import.file.tooManyRows';
  if (reason === 'too_many_columns') return 'admin.prospection.import.file.tooManyColumns';
  if (reason === 'cell_too_large') return 'admin.prospection.import.file.cellTooLarge';
  if (reason === 'empty_workbook' || reason === 'no_usable_worksheet' || reason === 'empty_csv') {
    return 'admin.prospection.import.file.empty';
  }
  if (reason === 'invalid_excel' || reason === 'invalid_csv') return 'admin.prospection.import.file.invalid';
  if (reason === 'object_missing') return 'admin.prospection.import.file.missing';
  if (reason === 'import_expired' || reason === 'upload_expired') {
    return 'admin.prospection.import.file.expired';
  }
  if (reason === 'storage_sign_unavailable' || reason === 'parse_unavailable') {
    return 'admin.prospection.import.file.unavailable';
  }
  if (error?.code === 'upload_failed') return 'admin.prospection.import.file.uploadFailed';
  const message = String(error?.message || '');
  if (/8 Mo|8 MiB|8 MB/i.test(message)) return 'admin.prospection.import.file.tooLarge';
  if (/10 000|10000/i.test(message)) return 'admin.prospection.import.file.tooManyRows';
  return errorKeyFor(error);
}

function errorKeyFor(error) {
  const details = callableDetails(error);
  if (details?.reason === 'async_enqueue_failed' || details?.reason === 'enqueue_failed') {
    return 'admin.prospection.import.async.enqueueFailed';
  }
  const kind = classifyAdminCallableError(error);
  if (kind === 'unauthenticated') return 'admin.prospection.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.error.permission';
  if (kind === 'invalid-argument') return 'admin.prospection.error.invalid';
  if (kind === 'not-found') return 'admin.prospection.error.notFound';
  if (kind === 'failed-precondition') return 'admin.prospection.error.failedPrecondition';
  if (kind === 'unavailable') return 'admin.prospection.error.unavailable';
  if (kind === 'internal') return 'admin.prospection.error.internal';
  return 'admin.prospection.error';
}

export { formatImportFlash, IMPORT_ROW_LIMIT };
